import React, { useRef, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import bannerUploadIcon from './banner-upload-icon.svg';
import { Button, Checkbox, Icon, Input, Select } from '../../components';
import { config } from '../../api/config';
import {
  BANNER_DEEPLINK_OPTIONS,
  BANNER_LOCATION_OPTIONS,
} from './banners.data';
import styles from './banners.module.scss';

const Form = ({
  values,
  onChange,
  onSubmit,
  onToggleDiscount,
  onLocationToggle,
  onFileUploaded,
  onFileDeleted,
  discountOptions,
  discountFieldsLocked,
  loading,
  imageUploading,
  dateToMin,
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleUploadClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleInputUpload = event => {
    const file = event.target.files[0];

    if (file) {
      onFileUploaded(file);
    }
  };

  const handleDrop = event => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];

    if (file) {
      onFileUploaded(file);
    }
  };

  const renderSelectField = ({
    name,
    label,
    selectedValue,
    options,
    disabled = loading,
  }) => (
    <div className={styles.fieldGroup}>
      <div className={styles.fieldLabel}>{label}</div>
      <Select
        name={name}
        onChange={onChange}
        selectedValue={selectedValue}
        options={options}
        disabled={disabled}
        className={styles.fieldControl}
      />
    </div>
  );

  return (
    <form onSubmit={onSubmit}>
      <div className={styles.cardContent}>
        <div className={styles.formLayout}>
          <div className={styles.formColumn}>
            <h3 className={styles.sectionTitle}>Informacija</h3>
            <div className={styles.checkboxRow}>
              <Checkbox
                label="Nuolaida"
                name="hasDiscount"
                checked={values.hasDiscount}
                value={values.hasDiscount}
                onChange={onToggleDiscount}
                dense={true}
              />
            </div>

            {values.hasDiscount &&
              renderSelectField({
                name: 'discountId',
                label: 'Pasirinkite nuolaidą',
                selectedValue: values.discountId,
                options: discountOptions,
                disabled: loading,
              })}

            <Input
              name="title"
              label="Pavadinimas"
              type="text"
              disabled={loading || discountFieldsLocked}
              onChange={onChange}
              value={values.title}
              classes={{ inputGroup: styles.fieldGroup }}
            />

            <div className={styles.twoColumns}>
              <Input
                name="dateFrom"
                label="Laikotarpis nuo"
                type="date"
                max="2999-12-31"
                disabled={loading || discountFieldsLocked}
                onChange={onChange}
                value={values.dateFrom}
                classes={{ inputGroup: styles.fieldGroup }}
              />
              <Input
                name="dateTo"
                label="Laikotarpis iki"
                type="date"
                min={dateToMin}
                max="2999-12-31"
                disabled={loading || discountFieldsLocked}
                onChange={onChange}
                value={values.dateTo}
                classes={{ inputGroup: styles.fieldGroup }}
              />
            </div>

            <div className={styles.twoColumns}>
              <Input
                name="timeFrom"
                label="Valandos nuo (neprivaloma)"
                type="time"
                disabled={loading}
                onChange={onChange}
                value={values.timeFrom}
                classes={{ inputGroup: styles.fieldGroup }}
              />
              <Input
                name="timeTo"
                label="Valandos iki (neprivaloma)"
                type="time"
                disabled={loading}
                onChange={onChange}
                value={values.timeTo}
                classes={{ inputGroup: styles.fieldGroup }}
              />
            </div>
            {!values.timeFrom && !values.timeTo && (
              <p className={styles.helperText}>
                Jei laikas nenurodytas, baneris rodomas visą dieną.
              </p>
            )}

            {!values.hasDiscount &&
              renderSelectField({
                name: 'deepLink',
                label: 'Deeplink nuoroda',
                selectedValue: values.deepLink,
                options: BANNER_DEEPLINK_OPTIONS,
                disabled: loading,
              })}
          </div>

          <div className={styles.uploadColumn}>
            <input
              ref={inputRef}
              className={styles.hiddenInput}
              accept="image/*"
              type="file"
              onChange={handleInputUpload}
            />

            <div
              className={cn(styles.uploadBox, isDragging && styles.dragging)}
              onClick={handleUploadClick}
              onDragOver={event => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={event => {
                event.preventDefault();
                setIsDragging(false);
              }}
              onDrop={handleDrop}
              role="presentation"
            >
              {values.image?.url ? (
                <div className={styles.previewWrapper}>
                  <img
                    src={values.image.url}
                    alt={values.image.name || values.title || 'Banner'}
                    className={styles.previewImage}
                  />
                  <Button
                    color="dim"
                    type="button"
                    classes={{ button: styles.deleteButton }}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      onFileDeleted();
                    }}
                  >
                    Ištrinti
                  </Button>
                </div>
              ) : (
                <div className={styles.uploadPlaceholder}>
                  <img
                    src={bannerUploadIcon}
                    alt=""
                    className={styles.uploadIcon}
                  />
                  <span className={styles.uploadText}>
                    Įkelti banerio nuotrauką
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.locationsSection}>
          <h3 className={styles.sectionTitle}>Kur bus rodoma?</h3>

          <div className={styles.locationList}>
            {BANNER_LOCATION_OPTIONS.map(location => (
              <Checkbox
                key={location.value}
                label={location.label}
                name={location.value}
                checked={values.placements.includes(location.value)}
                value={values.placements.includes(location.value)}
                onChange={() => onLocationToggle(location.value)}
                success={values.placements.includes(location.value)}
                dense={true}
                disabled={location.disabled || loading}
              />
            ))}
          </div>
        </div>

        <div className={styles.divider} />

        <div className={styles.footer}>
          <Button
            color="outline"
            url={config.APP_PATHS.banners}
            classes={{ button: styles.footerButton }}
          >
            Atgal
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={loading || imageUploading}
            classes={{ button: styles.footerButton }}
          >
            Išsaugoti
          </Button>
        </div>
      </div>
    </form>
  );
};

Form.propTypes = {
  values: PropTypes.shape({
    title: PropTypes.string.isRequired,
    hasDiscount: PropTypes.bool.isRequired,
    discountId: PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.number.isRequired,
    ]).isRequired,
    deepLink: PropTypes.string.isRequired,
    dateFrom: PropTypes.string.isRequired,
    dateTo: PropTypes.string.isRequired,
    timeFrom: PropTypes.string,
    timeTo: PropTypes.string,
    placements: PropTypes.arrayOf(PropTypes.string).isRequired,
    image: PropTypes.object,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onToggleDiscount: PropTypes.func.isRequired,
  onLocationToggle: PropTypes.func.isRequired,
  onFileUploaded: PropTypes.func.isRequired,
  onFileDeleted: PropTypes.func.isRequired,
  discountOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  discountFieldsLocked: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  imageUploading: PropTypes.bool.isRequired,
  dateToMin: PropTypes.string,
};

export default Form;
